import { useCallback, useEffect, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../utils/axiosConfig';
import { cn } from '../../lib/utils';
import { resolveRunPreviewSplits } from '../../utils/runningWorkoutFlatten';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Search } from 'lucide-react';
import RunningExerciseCard from './RunningExerciseCard';
import RunningExerciseDetail from './RunningExerciseDetail';

function formatDurationSec(sec) {
  if (sec == null || !Number.isFinite(Number(sec))) return '—';
  const s = Math.round(Number(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
  return `${m}:${String(r).padStart(2, '0')}`;
}

function previewSplitsForFavoriteExercise(ex) {
  return resolveRunPreviewSplits(ex.params, ex.structure);
}

export default function RunningExerciseTemplatePickerModal({ open, onOpenChange, onPickTemplate }) {
  const { t } = useLanguage();
  const [scope, setScope] = useState('global');
  const [subtypes, setSubtypes] = useState([]);
  const [subtypeFilter, setSubtypeFilter] = useState('');
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [favoritesLoading, setFavoritesLoading] = useState(false);
  const pageSize = 48;
  const [hasMore, setHasMore] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailSessionId, setDetailSessionId] = useState(null);
  const [detailTrainingExercise, setDetailTrainingExercise] = useState(null);
  const [detailTitle, setDetailTitle] = useState('');

  useEffect(() => {
    const tmr = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(tmr);
  }, [q]);

  useEffect(() => {
    if (!open) return;
    api
      .get('/api/training/running-workout-subtypes')
      .then((res) => setSubtypes(res.data.subtypes || []))
      .catch(() => setSubtypes([]));
  }, [open]);

  const fetchSessionScope = useCallback(
    async (url, offset) => {
      const params = { limit: pageSize, offset };
      if (debouncedQ) params.q = debouncedQ;
      if (subtypeFilter) params.subtype_key = subtypeFilter;
      const res = await api.get(url, { params });
      const sessions = res.data.sessions || [];
      const hasMoreNext =
        url.includes('planner-running-sessions') && res.data.hasMore != null
          ? !!res.data.hasMore
          : sessions.length >= pageSize;
      return {
        nextRows: sessions.map((sess) => ({
          key: sess.id != null ? `sess-${sess.id}` : `sess-ex-${sess.exercise_id}`,
          name: sess.name,
          description: sess.description || '',
          exerciseId: sess.exercise_id,
          preview_splits: sess.preview_splits || [],
          sessionId: sess.id,
          needsPlannerSession: !!sess.needs_planner_session,
          trainingExercise: null,
          subtype_keys: sess.subtype_keys || [],
          estimated_duration_sec: sess.estimated_duration_sec,
          weighted_intensity: sess.weighted_intensity,
        })),
        hasMore: hasMoreNext,
      };
    },
    [debouncedQ, subtypeFilter, pageSize]
  );

  useEffect(() => {
    if (!open) return;
    if (scope === 'favorites') {
      let cancelled = false;
      setFavoritesLoading(true);
      setRows([]);
      setHasMore(false);
      (async () => {
        try {
          const params = { type: 'running', limit: 100, offset: 0 };
          if (debouncedQ) params.q = debouncedQ;
          const res = await api.get('/api/training/exercises', { params });
          if (cancelled) return;
          const list = (res.data.exercises || []).filter(
            (ex) =>
              ex.is_favorite &&
              ex.advanced_blocks &&
              ex.exercise_type === 'running' &&
              ex.structure &&
              Array.isArray(ex.structure.groups) &&
              ex.structure.groups.length > 0
          );
          setRows(
            list.map((ex) => ({
              key: `fav-${ex.id}`,
              name: ex.name,
              description: (ex.notes && String(ex.notes).trim()) || '',
              exerciseId: ex.id,
              preview_splits: previewSplitsForFavoriteExercise(ex),
              sessionId: null,
              trainingExercise: ex,
              subtype_keys: [],
              estimated_duration_sec: null,
              weighted_intensity: null,
            }))
          );
        } catch (e) {
          console.error(e);
          if (!cancelled) setRows([]);
        } finally {
          if (!cancelled) setFavoritesLoading(false);
        }
      })();
      return () => {
        cancelled = true;
      };
    }

    let cancelled = false;
    setLoading(true);
    setRows([]);
    const url =
      scope === 'global' ? '/api/training/running-sessions' : '/api/training/planner-running-sessions';
    fetchSessionScope(url, 0)
      .then(({ nextRows, hasMore: hm }) => {
        if (cancelled) return;
        setRows(nextRows);
        setHasMore(hm);
      })
      .catch((e) => {
        console.error(e);
        if (!cancelled) {
          setRows([]);
          setHasMore(false);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, scope, debouncedQ, subtypeFilter, fetchSessionScope]);

  useEffect(() => {
    if (!open) {
      setQ('');
      setSubtypeFilter('');
      setScope('global');
      setDetailOpen(false);
      setDetailSessionId(null);
      setDetailTrainingExercise(null);
    }
  }, [open]);

  const loadMore = () => {
    if (scope === 'favorites') return;
    setLoading(true);
    const url =
      scope === 'global' ? '/api/training/running-sessions' : '/api/training/planner-running-sessions';
    fetchSessionScope(url, rows.length)
      .then(({ nextRows, hasMore: hm }) => {
        setRows((prev) => [...prev, ...nextRows]);
        setHasMore(hm);
      })
      .catch(() => setHasMore(false))
      .finally(() => setLoading(false));
  };

  const showSubtypeFilter = scope === 'global' || scope === 'mine';
  const busy = scope === 'favorites' ? favoritesLoading : loading;

  const handlePick = (row) => {
    if (!row.exerciseId) return;
    onPickTemplate?.(row.exerciseId);
    onOpenChange(false);
  };

  const openDetail = async (row) => {
    setDetailTitle(row.name || '');
    if (row.sessionId != null) {
      setDetailSessionId(row.sessionId);
      setDetailTrainingExercise(null);
      setDetailOpen(true);
      return;
    }
    setDetailSessionId(null);
    if (row.exerciseId) {
      try {
        const res = await api.get(`/api/training/exercises/${row.exerciseId}`);
        setDetailTrainingExercise(res.data?.exercise || null);
        setDetailOpen(true);
      } catch (e) {
        console.error(e);
      }
    } else {
      setDetailTrainingExercise(row.trainingExercise);
      setDetailOpen(true);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          priority="nested"
          className="max-w-[min(90vw,960px)] max-h-[min(90vh,820px)] flex flex-col gap-0 p-0 overflow-hidden"
        >
          <DialogHeader className="px-6 pt-6 pb-3 shrink-0 border-b border-border">
            <DialogTitle>{t('runningExercise.templatePickerTitle')}</DialogTitle>
            <DialogDescription>{t('runningExercise.templatePickerDescription')}</DialogDescription>
          </DialogHeader>

          <div className="px-6 py-3 space-y-3 shrink-0 border-b border-border bg-muted/10">
            <div className="flex flex-wrap gap-1">
              <Button
                type="button"
                size="sm"
                variant={scope === 'global' ? 'secondary' : 'ghost'}
                className="h-8 text-xs"
                onClick={() => setScope('global')}
              >
                {t('runningExercise.templateScopeGlobal')}
              </Button>
              <Button
                type="button"
                size="sm"
                variant={scope === 'mine' ? 'secondary' : 'ghost'}
                className="h-8 text-xs"
                onClick={() => setScope('mine')}
              >
                {t('runningExercise.templateScopeMine')}
              </Button>
              <Button
                type="button"
                size="sm"
                variant={scope === 'favorites' ? 'secondary' : 'ghost'}
                className="h-8 text-xs"
                onClick={() => setScope('favorites')}
              >
                {t('runningExercise.templateScopeFavorites')}
              </Button>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input
                  className="pl-8"
                  placeholder={t('runningExercise.templateSearchPlaceholder')}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
              </div>
              {showSubtypeFilter && (
                <div className="w-full sm:w-[220px] space-y-1">
                  <Label className="text-xs text-muted-foreground">{t('training.sessionPicker.subtype')}</Label>
                  <Select
                    value={subtypeFilter || '__all'}
                    onValueChange={(v) => setSubtypeFilter(v === '__all' ? '' : v)}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder={t('training.sessionPicker.allSubtypes')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all">{t('training.sessionPicker.allSubtypes')}</SelectItem>
                      {subtypes.map((st) => (
                        <SelectItem key={st.id} value={st.subtype_key}>
                          {st.label_key ? t(st.label_key) : st.subtype_key}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
            {busy && rows.length === 0 ? (
              <div className="flex justify-center py-16">
                <Loader2 className="h-10 w-10 animate-spin text-primary" />
              </div>
            ) : rows.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-12">
                {t('runningExercise.templatePickerEmpty')}
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {rows.map((row) => (
                  <div
                    key={row.key}
                    className={cn(
                      'rounded-xl border border-border bg-card shadow-sm',
                      'transition-all duration-200 hover:border-primary/40 hover:shadow-md'
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => handlePick(row)}
                      className="w-full text-left rounded-t-xl p-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="text-sm font-semibold text-foreground line-clamp-2 leading-tight">
                          {row.name}
                        </h3>
                        {row.weighted_intensity != null && (
                          <Badge variant="secondary" className="shrink-0 text-[10px] tabular-nums">
                            {t('training.sessionPicker.intensity')}{' '}
                            {(Number(row.weighted_intensity) * 10).toFixed(1)}
                          </Badge>
                        )}
                      </div>
                      {!!row.description && (
                        <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{row.description}</p>
                      )}
                      <div className="mt-2 flex flex-wrap gap-1">
                        {row.estimated_duration_sec != null && (
                          <Badge variant="outline" className="text-[10px] font-normal">
                            {formatDurationSec(row.estimated_duration_sec)}
                          </Badge>
                        )}
                        {(row.subtype_keys || []).slice(0, 4).map((k) => (
                          <Badge key={k} variant="outline" className="text-[10px] font-normal capitalize">
                            {k.replace(/_/g, ' ')}
                          </Badge>
                        ))}
                      </div>
                    </button>
                    <div className="px-3 pb-3">
                      <RunningExerciseCard
                        className="border-0 shadow-none bg-transparent p-0"
                        title={null}
                        description={null}
                        splits={row.preview_splits || []}
                        vizHeightPx={40}
                        onOpenDetail={() => openDetail(row)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
            {hasMore && rows.length > 0 && scope !== 'favorites' && (
              <div className="flex justify-center pt-4 pb-2">
                <Button type="button" variant="outline" size="sm" disabled={loading} onClick={loadMore}>
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t('training.sessionPicker.loadMore')}
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <RunningExerciseDetail
        open={detailOpen}
        onOpenChange={setDetailOpen}
        mode={detailSessionId ? 'library' : 'trainingExercise'}
        librarySessionId={detailSessionId}
        trainingExercise={detailTrainingExercise}
        fallbackTitle={detailTitle}
      />
    </>
  );
}
